import axios from 'axios';

const BASE_PATH = process.env.REACT_APP_BASE_PATH || 'https://ce29bycsq9.execute-api.eu-west-1.amazonaws.com/prod';

// Payment API configuration (from docs) - only use in production or when explicitly enabled
const USE_STRIPE_INTEGRATION = process.env.REACT_APP_USE_STRIPE_INTEGRATION === 'true';
const DISABLE_GIVEFUL_LOGIN = process.env.REACT_APP_DISABLE_GIVEFUL_LOGIN === 'true';
const PAYMENT_API_BASE_URL = process.env.REACT_APP_PAYMENT_API_BASE_URL || 'https://ce29bycsq9.execute-api.eu-west-1.amazonaws.com/prod';
const PAYMENT_API_KEY = process.env.REACT_APP_PAYMENT_API_KEY || 'c7hwe67ccd';
const TENANT_ID = process.env.REACT_APP_TENANT_ID || '3e79a4b8-e3d5-4641-a3dd-cc08208e5269';

// Create axios instance with default configuration - use BASE_PATH for all API calls
const api = axios.create({
  baseURL: BASE_PATH,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': TENANT_ID,
  },
});

// Create separate instance for authentication (using basePath)
const authApi = axios.create({
  baseURL: BASE_PATH,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate instance for payments (conditional based on environment)
const paymentsApi = axios.create({
  baseURL: USE_STRIPE_INTEGRATION ? PAYMENT_API_BASE_URL : BASE_PATH,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    ...(USE_STRIPE_INTEGRATION ? {
      'x-api-key': PAYMENT_API_KEY,
      'x-tenant-id': TENANT_ID,
    } : {})
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: any) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error: any) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: any) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: any) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Authentication interfaces
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role?: string;
  };
  expiresAt: string;
}

export interface AnonymousUser {
  username: string;
  password: string;
}

// Payment interfaces
export interface CreatePaymentDto {
  amount: number;
  currency: string;
  description?: string;
  metadata?: Record<string, any>;
}

export interface CheckoutDto {
  title: string;
  description?: string;
  amount: number;
  companyId: string;
  paymentMethods?: any[];
  paymentOptions?: any[];
  redirectTo?: string;
  type?: string;
  freeValue?: boolean;
}

export interface CheckoutSessionDto {
  checkoutId: string;
  userId: string;
  params?: {
    reference?: string;
    eventRegistration?: boolean;
    externalId?: string;
    amount?: number;
    userEmail?: string;
    eventId?: string;
    userName?: string;
    lastName?: string;
    checkout?: any;
    orderId?: string;
    user?: string;
    requiresTicket?: boolean;
    referenceId?: string;
    comment?: string;
    type?: string;
    paymentOptionChoosed?: any;
    name?: string;
    email?: string;
    phone?: string;
  };
}

// New Stripe Integration Interfaces
export interface TicketSessionParams {
  eventFlow: boolean;
  externalId: string;
  amount: number;
  currency: string;
  userEmail: string;
  eventId: string;
  userName: string;
  eventTitle: string;
  eventDescription: string;
  eventDate: string;
  eventLocation: string;
  ticketCategories: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    currency: string;
  }>;
  ticketHolders: Array<{
    ticketNumber: number;
    name: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
    guardianName?: string;
    guardianEmail?: string;
    guardianPhone?: string;
  }>;
  totalTickets: number;
  referenceId: string;
  checkout: {
    provider: string;
    successUrl: string;
    cancelUrl: string;
  };
}

export interface CreateTicketSessionRequest {
  checkoutId: string;
  userId: string;
  params: TicketSessionParams;
}

export interface CreateTicketSessionResponse {
  success: boolean;
  sessionId: string;
  expiresAt: string;
}

export interface CreateStripePaymentRequest {
  amount: number;
  currency: string;
  provider: string;
  ticketQuantity: number;
  userEmail: string;
  sessionId: string;
  metadata: {
    eventId: string;
    userId: string;
    userName: string;
    eventTitle: string;
    sessionReference?: string;
  };
}

export interface StripePaymentResponse {
  success: boolean;
  message: string;
  payment: {
    id: string;
    orderId: string;
    amount: number;
    currency: string;
    provider: string;
    status: string;
    providerPaymentId: string;
    metadata: Record<string, any>;
    createdAt: string;
    updatedAt: string;
  };
  data: {
    checkoutUrl: string;
    sessionId: string;
    paymentUrl: string;
  };
}

// Types
export interface MediaItem {
  type: 'image' | 'video';
  url: string;
  description?: string;
}

export interface Geolocation {
  latitude: number;
  longitude: number;
  address?: string;
}

export interface CheckoutConfig {
  provider: string; // 'stripe', 'paypal', 'mercadopago', etc.
  publicKey?: string;
  webhookUrl?: string;
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, any>;
}

export interface AgeRestrictions {
  minAge?: number;
  maxAge?: number;
  requiresGuardian?: boolean;
  ageVerificationRequired?: boolean;
}

export interface TicketCategory {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  maxQuantity?: number;
  maxAttendees?: number;
  currentSold?: number;
  enabled: boolean;
  isFree: boolean;
  checkout?: CheckoutConfig;
  validFrom?: string;
  validUntil?: string;
  ageRestrictions?: AgeRestrictions;
  metadata?: Record<string, any>;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  location: string;
  ticketCategories: TicketCategory[];
  enabled: boolean;
  medias?: MediaItem[];
  mainBanner?: MediaItem;
  geolocation?: Geolocation;
  hideTicketOptionsCount?: boolean;
  paymentOptions?: PaymentOption[];
  createdAt: string;
  updatedAt: string;
  // Legacy fields for backward compatibility
  name?: string;
  dateTime?: string;
  price?: number;
  maxAttendees?: number;
  organizerId?: string;
}

export interface PaymentOption {
  id: string;
  name: string;
  type: 'stripe' | 'mbway' | 'bizum' | 'paypal' | 'other';
  description: string;
  icon: string;
  enabled: boolean;
  paymentUrl?: string;
  config?: Record<string, any>;
}

export interface Ticket {
  id: string;
  eventId: string;
  ticketCategoryId?: string;
  userId: string;
  userEmail: string;
  userName: string;
  userAge?: number;
  userDateOfBirth?: string;
  guardianConsent?: boolean;
  quantity: number;
  originalPrice: number;
  finalPrice: number;
  status: 'pending' | 'confirmed' | 'used' | 'cancelled';
  purchaseDate: string;
  eventTitle?: string;
  eventDate?: string;
  eventLocation?: string;
  category?: string;
  voucherCode?: string;
  discountAmount?: number;
  ttl?: number; // For auto-expiring pending tickets
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  age?: number;
  preferences?: Record<string, any>;
  createdAt: string;
}

export interface Voucher {
  id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discount: number;
  eventId?: string;
  maxUsage: number;
  currentUsage: number;
  validFrom: string;
  validTo: string;
  enabled: boolean;
  minPurchaseAmount?: number;
  isActive?: boolean;
  createdAt: string;
}

export interface TicketPurchaseRequest {
  eventId: string;
  ticketCategoryId?: string;
  userId: string;
  userEmail: string;
  userName: string;
  userAge?: number;
  userDateOfBirth?: string;
  guardianConsent?: boolean;
  quantity: number;
  voucherCode?: string;
}

export interface IndividualTicket {
  ticketCategoryId: string;
  holderName: string;
  holderEmail?: string;
  holderAge?: number;
  holderDateOfBirth?: string;
  guardianConsent?: boolean;
  quantity?: number;
}

export interface MultiTicketPurchaseRequest {
  eventId: string;
  purchaserUserId: string;
  purchaserEmail: string;
  purchaserName: string;
  tickets: IndividualTicket[];
  voucherCode?: string;
}

export interface VoucherValidationRequest {
  code: string;
  purchaseAmount: number;
  eventId?: string;
}

export interface TicketValidationRequest {
  ticketId: string;
}

// API Service Class
class ApiService {
  // Events
  async getEvents(): Promise<Event[]> {
    const response = await api.get('/events?enabled=true');
    return response.data as Event[];
  }

  async getEvent(id: string): Promise<Event> {
    const response = await api.get(`/events/${id}?enabled=true`);
    return response.data as Event;
  }

  async createEvent(event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<Event> {
    const response = await api.post('/events', event);
    return response.data as Event;
  }

  async updateEvent(id: string, event: Partial<Event>): Promise<Event> {
    const response = await api.put(`/events/${id}`, event);
    return response.data as Event;
  }

  async getEnabledEvents(): Promise<Event[]> {
    const response = await api.get('/events?enabled=true');
    return response.data as Event[];
  }

  async getEventStats(id: string): Promise<{ totalTickets: number; soldTickets: number; revenue: number }> {
    const response = await api.get(`/events/${id}/stats`);
    return response.data as { totalTickets: number; soldTickets: number; revenue: number };
  }

  async deleteEvent(id: string): Promise<void> {
    await api.delete(`/events/${id}`);
  }

  // Tickets
  async getTickets(): Promise<Ticket[]> {
    const response = await api.get('/tickets');
    return response.data as Ticket[];
  }

  async getTicket(id: string): Promise<Ticket> {
    const response = await api.get(`/tickets/${id}`);
    return response.data as Ticket;
  }

  async purchaseTicket(purchase: TicketPurchaseRequest): Promise<{ ticket: Ticket; paymentUrl?: string }> {
    const response = await api.post('/buy', purchase);
    return response.data as { ticket: Ticket; paymentUrl?: string };
  }

  async purchaseMultipleTickets(purchase: MultiTicketPurchaseRequest): Promise<{ tickets: Ticket[]; paymentUrl?: string }> {
    const response = await api.post('/buy/multi', purchase);
    return response.data as { tickets: Ticket[]; paymentUrl?: string };
  }

  async getTicketsByUser(userId: string): Promise<Ticket[]> {
    const response = await api.get(`/tickets/user/${userId}`);
    return response.data as Ticket[];
  }

  async getTicketsByEvent(eventId: string): Promise<Ticket[]> {
    const response = await api.get(`/tickets/event/${eventId}`);
    return response.data as Ticket[];
  }

  async validateTicket(validation: TicketValidationRequest): Promise<{ valid: boolean; ticket?: Ticket; message: string }> {
    const response = await api.post('/validate-ticket', validation);
    return response.data as { valid: boolean; ticket?: Ticket; message: string };
  }

  async useTicket(ticketId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/use-ticket', { ticketId });
    return response.data as { success: boolean; message: string };
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await authApi.post('/login', credentials);
    return response.data as LoginResponse;
  }

  async loginAnonymous(): Promise<LoginResponse> {
    // Skip Giveful login if disabled
    if (DISABLE_GIVEFUL_LOGIN) {
      console.log('Giveful login disabled - returning mock auth response');
      return {
        token: 'mock-token',
        user: {
          id: 'anonymous',
          email: 'anonymous@beholdit.pt',
          name: 'Anonymous User'
        },
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours from now
      };
    }
    
    const anonymousUser: AnonymousUser = {
      username: process.env.REACT_APP_ANONYMOUS_USER_EMAIL || 'anonymous@beholdit.pt',
      password: process.env.REACT_APP_ANONYMOUS_USER_PASSWORD || 'Y@VRK2HarEo&eC7pEmBo!knw'
    };
    return this.login(anonymousUser);
  }

  // Payments
  async createPayment(createPaymentDto: CreatePaymentDto): Promise<any> {
    const response = await paymentsApi.post('/payments', createPaymentDto);
    return response.data;
  }

  async getPaymentsByCompanyId(companyId: string, page = 0, limit = 15): Promise<any> {
    const response = await paymentsApi.get(`/payments/company/${companyId}?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getPaymentsByGroupId(groupId: string, page = 0, limit = 15): Promise<any> {
    const response = await paymentsApi.get(`/payments/group/${groupId}?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getPaymentById(paymentId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/${paymentId}`);
    return response.data;
  }

  async getCheckoutByCompanyId(companyId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/company/${companyId}`);
    return response.data;
  }

  async getCheckoutByEventId(eventId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/event/${eventId}`);
    return response.data;
  }

  async getCheckoutByGroupId(groupId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/group/${groupId}`);
    return response.data;
  }

  async getCheckoutById(id: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/detail/${id}`);
    return response.data;
  }

  // New Stripe Integration Methods (conditional)
  async createTicketSession(request: CreateTicketSessionRequest): Promise<CreateTicketSessionResponse> {
    if (!USE_STRIPE_INTEGRATION) {
      throw new Error('Stripe integration not enabled in development mode');
    }
    return this.retryApiCall(async () => {
      const response = await paymentsApi.post('/payments/checkout/session', request);
      return response.data as CreateTicketSessionResponse;
    }, 3);
  }

  async createStripePayment(request: CreateStripePaymentRequest): Promise<StripePaymentResponse> {
    if (!USE_STRIPE_INTEGRATION) {
      throw new Error('Stripe integration not enabled in development mode');
    }
    return this.retryApiCall(async () => {
      const response = await paymentsApi.post('/payments', request);
      return response.data as StripePaymentResponse;
    }, 3);
  }

  async createStripePaymentWithProgress(
    request: CreateStripePaymentRequest,
    onProgress?: (step: string, progress: number) => void
  ): Promise<StripePaymentResponse> {
    if (!USE_STRIPE_INTEGRATION) {
      throw new Error('Stripe integration not enabled in development mode');
    }

    onProgress?.('Creating payment session...', 25);
    await new Promise(resolve => setTimeout(resolve, 200));
    
    onProgress?.('Contacting Stripe...', 50);
    const result = await this.createStripePayment(request);
    
    onProgress?.('Payment session created', 75);
    await new Promise(resolve => setTimeout(resolve, 200));
    
    onProgress?.('Redirecting to Stripe...', 100);
    return result;
  }

  async retryApiCall<T>(
    apiCall: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (error: any) {
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Only retry on network errors or 5xx server errors
        const shouldRetry = !error.response || 
                           error.response.status >= 500 ||
                           error.code === 'NETWORK_ERROR' ||
                           error.code === 'TIMEOUT';
        
        if (!shouldRetry) {
          throw error;
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
        console.log(`API call failed (attempt ${attempt}/${maxRetries}). Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw new Error('Maximum retry attempts exceeded');
  }

  async pollPaymentStatus(
    sessionId: string,
    maxAttempts: number = 30,
    intervalMs: number = 1000
  ): Promise<{ status: string; payment?: any }> {
    if (!USE_STRIPE_INTEGRATION) {
      // Mock successful status for development
      await new Promise(resolve => setTimeout(resolve, 2000));
      return {
        status: 'confirmed',
        payment: {
          id: sessionId,
          status: 'confirmed',
          amount: 2500,
          currency: 'eur'
        }
      };
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await this.getPaymentByStripeSession(sessionId);
        
        if (result.success && result.payment) {
          const status = result.payment.status;
          
          // Terminal states
          if (status === 'confirmed' || status === 'failed' || status === 'cancelled') {
            return { status, payment: result.payment };
          }
          
          // Continue polling for pending states
          if (status === 'pending' && attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            continue;
          }
        }
        
        // If we reach here and it's the last attempt, return current state
        if (attempt === maxAttempts) {
          return { 
            status: result.payment?.status || 'unknown',
            payment: result.payment 
          };
        }
        
      } catch (error) {
        console.log(`Payment status check failed (attempt ${attempt}/${maxAttempts}):`, error);
        
        // If it's the last attempt, throw the error
        if (attempt === maxAttempts) {
          throw new Error('Payment status polling timeout');
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    
    throw new Error('Payment status polling timeout');
  }

  async getPaymentByStripeSession(sessionId: string): Promise<any> {
    if (!USE_STRIPE_INTEGRATION) {
      // Return mock data for development
      return {
        success: true,
        payment: {
          id: sessionId,
          amount: 2500, // €25.00 in cents
          currency: 'eur',
          status: 'confirmed',
          metadata: {
            eventTitle: 'Test Event',
            userName: 'Test User',
            userEmail: 'test@example.com'
          },
          createdAt: new Date().toISOString()
        }
      };
    }
    const response = await paymentsApi.get(`/payments/stripe/${sessionId}`);
    return response.data;
  }

  async getCheckoutByIdAndSessionId(id: string, sessionId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/detail/${id}?sessionId=${sessionId}`);
    return response.data;
  }

  async createCheckout(checkoutDto: CheckoutDto): Promise<any> {
    const response = await paymentsApi.post('/payments/checkout', checkoutDto);
    return response.data;
  }

  async createCheckoutSession(checkoutSessionDto: CheckoutSessionDto): Promise<any> {
    const response = await paymentsApi.post('/payments/checkout/session', checkoutSessionDto);
    return response.data;
  }

  async updateCheckoutSession(sessionId: string, checkoutSessionDto: Partial<CheckoutSessionDto>): Promise<any> {
    const response = await paymentsApi.put(`/payments/checkout/session/${sessionId}`, checkoutSessionDto);
    return response.data;
  }

  async getCheckoutSessionById(sessionId: string): Promise<any> {
    const response = await paymentsApi.get(`/payments/checkout/session/${sessionId}`);
    return response.data;
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await api.get('/users');
    return response.data as User[];
  }

  async getUser(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data as User;
  }

  async getUserByEmail(email: string): Promise<User> {
    const response = await api.get(`/users/by-email?email=${encodeURIComponent(email)}`);
    return response.data as User;
  }

  async createUser(user: Omit<User, 'id' | 'createdAt'>): Promise<User> {
    const response = await api.post('/users', user);
    return response.data as User;
  }

  async updateUser(id: string, user: Partial<User>): Promise<User> {
    const response = await api.put(`/users/${id}`, user);
    return response.data as User;
  }

  async getUserTickets(email: string): Promise<Ticket[]> {
    const response = await api.get(`/users/tickets?email=${encodeURIComponent(email)}`);
    return response.data as Ticket[];
  }

  async updateUserPreferences(id: string, preferences: Record<string, any>): Promise<User> {
    const response = await api.put(`/users/${id}/preferences`, { preferences });
    return response.data as User;
  }

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  }

  // Vouchers
  async getVouchers(): Promise<Voucher[]> {
    const response = await api.get('/vouchers');
    return response.data as Voucher[];
  }

  async getVoucher(id: string): Promise<Voucher> {
    const response = await api.get(`/vouchers/${id}`);
    return response.data as Voucher;
  }

  async createVoucher(voucher: Omit<Voucher, 'id' | 'createdAt' | 'currentUsage'>): Promise<Voucher> {
    const response = await api.post('/vouchers', { ...voucher, currentUsage: 0 });
    return response.data as Voucher;
  }

  async updateVoucher(id: string, voucher: Partial<Voucher>): Promise<Voucher> {
    const response = await api.put(`/vouchers/${id}`, voucher);
    return response.data as Voucher;
  }

  async getVouchersByEvent(eventId: string): Promise<Voucher[]> {
    const response = await api.get(`/vouchers/event/${eventId}`);
    return response.data as Voucher[];
  }

  async getActiveVouchers(): Promise<Voucher[]> {
    const response = await api.get('/vouchers?active=true');
    return response.data as Voucher[];
  }

  async deleteVoucher(id: string): Promise<void> {
    await api.delete(`/vouchers/${id}`);
  }

  async validateVoucher(validation: VoucherValidationRequest): Promise<{ valid: boolean; voucher?: Voucher; discount?: number; message: string }> {
    const response = await api.post('/vouchers/validate', validation);
    return response.data as { valid: boolean; voucher?: Voucher; discount?: number; message: string };
  }

  // Utility methods
  async calculateAge(dateOfBirth: string): Promise<number> {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  async validateAgeRestrictions(age: number, ageRestrictions: AgeRestrictions, guardianConsent?: boolean): Promise<{ isValid: boolean; reason?: string }> {
    if (!ageRestrictions) {
      return { isValid: true };
    }

    if (ageRestrictions.minAge && age < ageRestrictions.minAge) {
      return { 
        isValid: false, 
        reason: `Minimum age requirement is ${ageRestrictions.minAge} years. Current age: ${age}.` 
      };
    }

    if (ageRestrictions.maxAge && age > ageRestrictions.maxAge) {
      return { 
        isValid: false, 
        reason: `Maximum age limit is ${ageRestrictions.maxAge} years. Current age: ${age}.` 
      };
    }

    if (ageRestrictions.requiresGuardian && age < 18 && !guardianConsent) {
      return { 
        isValid: false, 
        reason: `Guardian consent is required for attendees under 18 years old.` 
      };
    }

    return { isValid: true };
  }

  // Stripe Integration Helper Methods
  async initiateStripeCheckout(
    event: Event,
    selectedTickets: Record<string, number>,
    ticketUserInfo: Record<string, any>,
    userEmail: string,
    onProgress?: (step: string, progress: number) => void
  ): Promise<string> {
    try {
      // In development mode, simulate the Stripe flow with mock data
      if (!USE_STRIPE_INTEGRATION) {
        console.log('Development mode: Simulating Stripe checkout...');
        
        onProgress?.('Simulating payment...', 50);
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        onProgress?.('Ready to redirect...', 100);
        
        // Return a mock Stripe URL (this would normally redirect to a test page)
        const mockSessionId = `cs_test_dev_${Date.now()}`;
        return `https://checkout.stripe.com/pay/${mockSessionId}#fidkdWxOYHwnPyd1blpxYHZxWjA0VGxfZ1F2PXZHbUU8Tj1%2FUkNSY2o2PTxNfHBEZj1lV2FtNTNjPT1ma1JLdDNOTFZVQ0lLa1RhMUNSTXRNQGNnNWRDbGFKYk9VV01lNVNkPUVnZktjYlBDNHxhRD17QycpJ2N3amhWYHdzYHcnP3F3cGApJ2lkfGpwcVF8dWAnPydocGlxbFpscWBoJyknYGtkZ2lgVWlkZmBtamlhYHd2Jz9xd3BgeCUl`;
      }

      onProgress?.('Preparing checkout data...', 10);

      // Production Stripe integration
      const totalAmount = this.calculateTotalAmount(event, selectedTickets);
      
      // Generate unique identifiers
      const checkoutId = `checkout-${Date.now()}`;
      const externalId = `frontend-${Date.now()}`;
      const referenceId = `ref-${Date.now()}`;
      
      // Prepare ticket categories
      const ticketCategories = Object.entries(selectedTickets)
        .filter(([_, quantity]) => quantity > 0)
        .map(([categoryId, quantity]) => {
          const category = event.ticketCategories?.find(c => c.id === categoryId);
          return {
            id: categoryId,
            name: category?.name || 'Unknown',
            price: Math.round((category?.price || 0) * 100), // Convert to cents
            quantity,
            currency: category?.currency || 'EUR'
          };
        });

      // Prepare ticket holders
      const ticketHolders = Object.entries(ticketUserInfo).map(([ticketKey, info], index) => ({
        ticketNumber: index + 1,
        name: info.name,
        email: info.email,
        phone: info.phone,
        dateOfBirth: info.dateOfBirth,
        guardianName: info.guardianName,
        guardianEmail: info.guardianEmail,
        guardianPhone: info.guardianPhone
      }));

      onProgress?.('Creating ticket session...', 25);

      // Step 1: Create ticket session
      const sessionRequest: CreateTicketSessionRequest = {
        checkoutId,
        userId: 'anonymous-user', // Will be handled by anonymous login
        params: {
          eventFlow: true,
          externalId,
          amount: totalAmount,
          currency: 'EUR',
          userEmail,
          eventId: event.id,
          userName: ticketHolders[0]?.name || 'Anonymous',
          eventTitle: event.title,
          eventDescription: event.description,
          eventDate: event.startDateTime,
          eventLocation: event.location,
          ticketCategories,
          ticketHolders,
          totalTickets: Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0),
          referenceId,
          checkout: {
            provider: 'stripe',
            successUrl: `${window.location.origin}/checkout/success/{CHECKOUT_SESSION_ID}?sessionId={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/checkout/cancel?sessionId={CHECKOUT_SESSION_ID}`
          }
        }
      };

      const sessionResponse = await this.createTicketSession(sessionRequest);

      onProgress?.('Session created, preparing payment...', 50);

      // Step 2: Create Stripe payment
      const paymentRequest: CreateStripePaymentRequest = {
        amount: totalAmount,
        currency: 'EUR',
        provider: 'stripe',
        ticketQuantity: Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0),
        userEmail,
        sessionId: sessionResponse.sessionId,
        metadata: {
          eventId: event.id,
          userId: 'anonymous-user',
          userName: ticketHolders[0]?.name || 'Anonymous',
          eventTitle: event.title,
          sessionReference: referenceId
        }
      };

      const paymentResponse = await this.createStripePaymentWithProgress(paymentRequest, onProgress);

      return paymentResponse.data.checkoutUrl;

    } catch (error: any) {
      console.error('Stripe checkout initiation failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to initiate payment');
    }
  }

  calculateTotalAmount(event: Event, selectedTickets: Record<string, number>): number {
    return Object.entries(selectedTickets).reduce((total, [categoryId, quantity]) => {
      const category = event.ticketCategories?.find(c => c.id === categoryId);
      if (category && !category.isFree) {
        return total + Math.round(category.price * quantity * 100); // Convert to cents
      }
      return total;
    }, 0);
  }

  generateTicketUserKey(categoryId: string, index: number): string {
    return `${categoryId}_${index}`;
  }
}

export const apiService = new ApiService();
export default api;
